/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable react/jsx-props-no-spreading */
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form } from 'informed';
import { Button } from '@unbooking/ui-modules';
import { TextField } from '@ui-modules/informed';
import {
  clearFormatNumber,
  maskPercentage,
  maskSalary,
  percentageField,
  requiredField,
} from '@common/utils';
import { IDriveCosts, IDriveCostsApi } from '@common/interfaces';
import { Edit } from '@assets/svg/icons';
import './styles.scss';

interface ICostField {
  className?: string;
  disabled?: boolean;
  label: string;
  name: keyof IDriveCosts;
  type?: string;
  mask?: (value: string) => string;
  validate?: ((value: unknown, values: Record<string, unknown>) => unknown) | undefined;
}

interface IFormState {
  averageKmPerHour: number;
  basicPercentage: number;
  carpoolingPercentage: number;
  dieselCost: number;
  grossSalary: number;
  kgCo2EmissionDiesel: number;
  kgCo2EmissionPetrol: number;
  perHour: number;
  perKmDiesel: number;
  perKmPetrol: number;
  percentageOfBuffer: number;
  petrolCost: number;
  rideSharingPercentage: number;
}

interface DriveCostsComponent {
  data: IDriveCosts;
  updateDriveCosts?: (data: Partial<IDriveCostsApi>) => void;
}

const DriveCosts: FC<DriveCostsComponent> = ({ data, updateDriveCosts }) => {
  const { t } = useTranslation();

  const [isEdit, setIsEdit] = useState<boolean>(false);

  const CostField: FC<ICostField> = ({
    className,
    disabled,
    label,
    name,
    type,
    mask,
    validate,
    ...rest
  }) => (
    <div className={className ? `field ${className}` : 'field'}>
      <TextField
        disabled={disabled}
        label={label}
        name={name}
        initialValue={`${data[name]}`}
        type={type}
        validateOnBlur
        mask={mask}
        validate={validate}
        {...rest}
      />
    </div>
  );

  return (
    <Form
      className="drive-costs-form"
      onSubmit={(formState: Record<string, unknown>) => {
        const state = { ...(formState.values as IFormState) };

        setIsEdit(false);
        updateDriveCosts?.({
          agency: data.agency,
          average_km_per_hour: state.averageKmPerHour,
          basic_percentage: state.basicPercentage,
          carpooling_percentage: state.carpoolingPercentage,
          diesel_cost: state.dieselCost,
          gross_salary: clearFormatNumber(state.grossSalary),
          kg_co_2_emission_per_liter_diesel: state.kgCo2EmissionDiesel,
          kg_co_2_emission_per_liter_petrol: state.kgCo2EmissionPetrol,
          location: data.location,
          percentage_of_buffer: state.percentageOfBuffer,
          petrol_cost: state.petrolCost,
          ride_sharing_percentage: state.rideSharingPercentage,
        });
      }}
    >
      <div className="row">
        <CostField
          className="field-currency"
          data-testid="drive-cost-salary"
          disabled={!isEdit}
          label={t('mobility.inputDriverGrossSalary')}
          name="grossSalary"
          mask={(value) => maskSalary(value as string)}
          validate={requiredField}
        />

        <CostField
          className="field-currency"
          data-testid="drive-cost-diesel-lt"
          disabled={!isEdit}
          label={t('mobility.inputDiesel')}
          name="dieselCost"
          validate={requiredField}
        />

        <CostField
          className="field-currency"
          data-testid="drive-cost-petrol-lt"
          disabled={!isEdit}
          label={t('mobility.inputPetrol')}
          name="petrolCost"
          validate={requiredField}
        />

        <CostField
          disabled={!isEdit}
          label={`${t('mobility.inputGlobal')} %`}
          name="basicPercentage"
          type="number"
          mask={(value) => maskPercentage(value as string)}
          validate={(value) => percentageField(value as string)}
        />

        <CostField
          disabled={!isEdit}
          label={`${t('mobility.inputCarpooling')} %`}
          name="carpoolingPercentage"
          type="number"
          mask={(value) => maskPercentage(value as string)}
          validate={(value) => percentageField(value as string)}
        />

        <CostField
          disabled={!isEdit}
          label={`${t('report.rideSharing')} %`}
          name="rideSharingPercentage"
          type="number"
          mask={(value) => maskPercentage(value as string)}
          validate={(value) => percentageField(value as string)}
        />

        <div className="field">
          {isEdit ? (
            <Button
              className="btn"
              key="submit"
              text={t('mobility.btnSaveSettings')}
              type="submit"
              variant="submit"
            />
          ) : (
            <Button
              className="btn btn-edit"
              icon={<Edit />}
              key="button"
              text={t('common.btnEdit')}
              type="button"
              variant="transparent"
              onClick={() => setIsEdit(true)}
            />
          )}
        </div>
      </div>
      <div className="row">
        <CostField
          disabled={!isEdit}
          label={t('mobility.inputAverageKmPerHour')}
          name="averageKmPerHour"
          type="number"
        />

        <CostField
          disabled={!isEdit}
          label={t('mobility.inputEmissionDiesel')}
          name="kgCo2EmissionDiesel"
          type="number"
        />

        <CostField
          disabled={!isEdit}
          label={t('mobility.inputEmissionPetrol')}
          name="kgCo2EmissionPetrol"
          type="number"
        />

        <CostField
          disabled={!isEdit}
          label={`${t('mobility.inputBuffer')} %`}
          name="percentageOfBuffer"
          type="number"
        />

        <CostField
          className="field-currency"
          disabled
          label={t('mobility.inputPerHour')}
          name="perHour"
          type="number"
        />

        <CostField
          className="field-currency"
          disabled
          label={t('mobility.inputPerKmDiesel')}
          name="perKmDiesel"
          type="number"
        />

        <CostField
          className="field-currency"
          disabled
          label={t('mobility.inputPerKmPetrol')}
          name="perKmPetrol"
          type="number"
        />
      </div>
    </Form>
  );
};

export default DriveCosts;
